import { createFileRoute, Outlet, Link } from '@tanstack/react-router';
import { UserCircle, Lock, Mail, ShieldCheck } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { SlimFooter } from '@/components/SlimFooter';
import { requireAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/account')({
  beforeLoad: ({ context, location }) => requireAuth({ context, location }),
  component: AccountLayout,
});

interface NavItem {
  to: string;
  label: string;
  icon: typeof UserCircle;
  exact?: boolean;
}
const NAV_ITEMS: NavItem[] = [
  { to: '/account', label: 'Profile', icon: UserCircle, exact: true },
  { to: '/account/change-password', label: 'Password', icon: Lock },
  { to: '/account/email', label: 'Email', icon: Mail },
  { to: '/account/2fa', label: 'Two-factor', icon: ShieldCheck },
];

function AccountLayout() {
  return (
    <div className="flex min-h-full flex-col bg-(--color-paper)">
      <AppHeader
        authed
        crumbs={[
          { label: 'My returns', to: '/returns' },
          { label: 'Account' },
        ]}
      />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-6 py-12 lg:grid lg:grid-cols-[16rem_1fr] lg:gap-12">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="section-ref">Account</div>
            <h1 className="mt-2 font-serif text-3xl font-medium tracking-tight">
              Your details
            </h1>
            <p className="mt-2 text-[13px] text-(--color-ink-muted)">
              Manage how you sign in, recover access, and keep this account secure.
            </p>
            <nav className="mt-7 space-y-0.5 border-l border-(--color-rule)" aria-label="Account sections">
              {NAV_ITEMS.map(({ to, label, icon: Icon, exact }) => (
                <Link
                  key={to}
                  to={to}
                  activeOptions={exact ? { exact: true } : undefined}
                  className={cn(
                    '-ml-px flex items-center gap-3 border-l-2 border-transparent px-4 py-2.5 text-[14px] text-(--color-ink-2)',
                    'hover:bg-(--color-paper-2)',
                  )}
                  activeProps={{
                    className: '-ml-px flex items-center gap-3 border-l-2 border-(--color-navy) bg-(--color-navy-soft)/60 px-4 py-2.5 text-[14px] font-semibold text-(--color-navy)',
                  }}
                >
                  <Icon className="size-4" strokeWidth={1.7} />
                  {label}
                </Link>
              ))}
            </nav>
          </aside>
          <div className="mt-10 lg:mt-0">
            <Outlet />
          </div>
        </div>
      </main>
      <SlimFooter />
    </div>
  );
}
